import { observer } from "mobx-react-lite";
import useStore from "reusable/mobx-store-utils";
import StoreRef from "reusable/mobx-store-utils/StoreRef";
import Stack from "@mui/material/Stack";
import { isFunctionalComponent } from "reusable/mobx-store-utils/utils/utils";
import { AppBarContainer, AppBarContainerProps } from "reusable/mui/app-bar-container/AppBarContainer";
import Dialog from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import StepperFullscreenStore, { StepperFullscreenStoreProvider, useStepperFullscreenStore } from "./StepperFullscreenStore";
import { StepperStep, Steps } from "../StepperShared";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import React, { useMemo } from "react";
import ButtonWithProgress from "reusable/mui/progress-button/ButtonWithProgress";
import { themeConfig } from '@gap/shared/theme';

export interface StepperModalFullscreenProps {
   storeRef?: StoreRef<StepperFullscreenStore>
   /** each view that will be rendered */
   steps: StepperStep[]
   appBarProps: AppBarContainerProps
   rightButton?: React.ReactNode
   stepMustBeMarkedAsDoneBeforeContinue?: boolean
   onStepChange?: () => void | Promise<void>,
   continueButtonIsLoading?: boolean,
}

export default observer(function StepperModalFullscreen(props: StepperModalFullscreenProps) {
   const stepperRootRef = React.useRef<HTMLDivElement>(null)
   const stepperFullscreenStore = useStore(sp => new StepperFullscreenStore(sp), { ...props, stepperRootRef })

   const Content = useMemo(() => {
      const _content = stepperFullscreenStore.activeStep?.content
      return isFunctionalComponent(_content) ? <_content /> : _content
   }, [stepperFullscreenStore.activeStep])

   const Sidebar = useMemo(() => {
      const _sidebar = stepperFullscreenStore.activeStep?.sidebar
      return isFunctionalComponent(_sidebar) ? <_sidebar /> : _sidebar
   }, [stepperFullscreenStore.activeStep])

   const border = `1px solid ${themeConfig.primary[200]}`

   return <Dialog fullScreen open sx={{ padding: '0 !important' }}>
      <StepperFullscreenStoreProvider store={stepperFullscreenStore}>
         <AppBarContainer {...props.appBarProps} noContentPadding>
            <Stack ref={stepperRootRef} sx={{ flexGrow: 1, alignSelf: 'stretch' }}>
               <Stack sx={{
                  borderBottom: border,
                  maxWidth: '100%',
                  overflowX: 'auto',
                  alignItems: 'center',
               }}>
                  <Stack sx={{
                     flexDirection: 'row',
                     gap: 4,
                     width: themeConfig.breakpoints.values.lg,
                     maxWidth: '100%',
                     px: { xs: 1, sm: 3 },
                     py: 2,
                  }}>
                     <Stack sx={{ flexDirection: 'column', flexGrow: 1, mx: -1 }}>
                        <Steps activeStepIndex={stepperFullscreenStore.activeStepIndex} steps={props.steps} orientation="horizontal" />
                     </Stack>
                     {props.rightButton &&
                        <Stack direction="column" sx={{ alignItems: 'flex-end', flex: '0 1' }}>
                           {props.rightButton}
                        </Stack>}
                  </Stack>
               </Stack>
               <Stack sx={{
                  flexGrow: 1,
                  flexDirection: {
                     md: 'column',
                     lg: 'row',
                  },
                  rowGap: 4,
                  justifyContent: 'center',
               }}>
                  <Stack direction="column" sx={{
                     flexGrow: '1',
                     gap: 3,
                     pb: { xs: 1, sm: 3 },
                     ...(Sidebar ? {} : {
                        maxWidth: themeConfig.breakpoints.values.lg,
                        width: '100%',
                     })
                  }}>
                     <Stack sx={{
                        flexGrow: 1,
                        px: { xs: 1, sm: 3 },
                        pt: { xs: 2, sm: 3 },
                        pb: 4,
                        maxHeight: '100%',
                        overflowY: 'auto',
                     }}>
                        {Content}
                     </Stack>
                     {stepperFullscreenStore.activeStep?.hideNavigation
                        ? undefined
                        : <Navigation />
                     }
                  </Stack>
                  {Sidebar &&
                     <Stack sx={{
                        flexShrink: 0,
                        px: { xs: 1, sm: 3 },
                        pt: { xs: 2, sm: 4 },
                        pb: { xs: 1, sm: 3 },
                        backgroundColor: themeConfig.light.background.alt,
                        borderTop: { xs: border, lg: 'none' },
                        borderLeft: { xs: 'none', lg: border },
                        maxWidth: { xs: 'none', lg: '410px' },
                     }}>
                        {Sidebar}
                     </Stack>
                  }
               </Stack>
            </Stack>
         </AppBarContainer>
      </StepperFullscreenStoreProvider>
   </Dialog>
})

const Navigation = observer(function Navigation() {
   const stepperFullscreenStore = useStepperFullscreenStore()

   return <Stack sx={{ flexDirection: 'row', justifyContent: 'right', marginX: { xs: 1, sm: 3 } }}>
      {stepperFullscreenStore.activeStepIndex > 0 && <Button onClick={stepperFullscreenStore.previousStep}>Back</Button>}
      {stepperFullscreenStore.showContinueButton && <ButtonWithProgress
         onClick={stepperFullscreenStore.nextStep}
         variant="contained"
         icon={<ArrowForwardIcon />}
         loading={stepperFullscreenStore.sp.continueButtonIsLoading}
         disabled={!stepperFullscreenStore.canWeGoToTheNextStep}
      >Continue</ButtonWithProgress>}
   </Stack>
})

